'use client'

import * as React from 'react'
import Link from 'next/link'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useNotifications } from '@/context/NotificationContext'
import { formatRelativeTime, severityMeta } from '@/lib/notification-format'
import type { AppNotification } from '@/types/notification'

interface NotificationItemProps {
  notification: AppNotification
  onMarkAsRead: (id: string) => void
  onNavigate: () => void
}

function NotificationItem({
  notification,
  onMarkAsRead,
  onNavigate,
}: NotificationItemProps) {
  const meta = severityMeta(notification.severity)
  const Icon = meta.icon

  const body = (
    <>
      <div className={cn('flex-shrink-0 rounded-lg p-2', meta.bg)}>
        <Icon
          className={cn('h-4 w-4', meta.color)}
          strokeWidth={1.5}
          aria-hidden='true'
        />
        <span className='sr-only'>{meta.label}</span>
      </div>
      <div className='min-w-0 flex-1'>
        <div className='flex items-start justify-between gap-2'>
          <p className='truncate text-sm font-medium text-foreground'>
            {notification.title}
          </p>
          {!notification.is_read && (
            <span
              className='mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-cornflower'
              aria-hidden='true'
            />
          )}
        </div>
        <p className='mt-0.5 line-clamp-2 text-xs text-muted-foreground'>
          {notification.message}
        </p>
        <p className='mt-1 text-[0.625rem] text-muted-foreground/60'>
          {formatRelativeTime(notification.created_at)}
          {!notification.is_read && <span className='sr-only'> — unread</span>}
        </p>
      </div>
    </>
  )

  const className = cn(
    'flex w-full gap-3 rounded-xl p-3 text-left',
    'transition-colors duration-200 ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower',
    notification.is_read
      ? 'opacity-60 hover:opacity-100'
      : 'hover:bg-brand-cornflower/5'
  )

  // Items with a target navigate; the rest are still keyboard-operable buttons
  // that only clear the unread state.
  if (notification.link) {
    return (
      <Link
        href={notification.link}
        className={className}
        onClick={() => {
          onMarkAsRead(notification.id)
          onNavigate()
        }}
      >
        {body}
      </Link>
    )
  }

  return (
    <button
      type='button'
      className={className}
      onClick={() => onMarkAsRead(notification.id)}
    >
      {body}
    </button>
  )
}

function NotificationSkeleton() {
  return (
    <div className='flex gap-3 p-3' role='status' aria-label='Loading notifications'>
      <Skeleton variant='circular' className='h-8 w-8 shrink-0' />
      <div className='flex-1 space-y-2'>
        <Skeleton variant='text' className='h-3 w-3/4' />
        <Skeleton variant='text' className='h-3 w-full' />
      </div>
    </div>
  )
}

export function NotificationCenter() {
  const [open, setOpen] = React.useState(false)
  const { notifications, unreadCount, loading, error, refresh, markAsRead, markAllAsRead } =
    useNotifications()

  // Pull the freshest list when the panel opens rather than on every poll tick.
  React.useEffect(() => {
    if (open) void refresh()
  }, [open, refresh])

  const showSkeleton = loading && notifications.length === 0

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          className='relative rounded-full text-muted-foreground hover:text-foreground'
          aria-label={
            unreadCount > 0
              ? `Notifications, ${unreadCount} unread`
              : 'Notifications, none unread'
          }
        >
          <Icons.bell className='h-5 w-5' strokeWidth={1.5} aria-hidden='true' />
          {unreadCount > 0 && (
            <span
              className={cn(
                'absolute -right-0.5 -top-0.5',
                'flex h-4 min-w-4 items-center justify-center',
                'rounded-full bg-destructive px-1',
                'text-[0.625rem] font-semibold leading-none text-destructive-foreground',
                'motion-safe:animate-scale-in'
              )}
              aria-hidden='true'
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-80 p-0'>
        <div className='flex items-center justify-between border-b border-border/50 px-4 py-3'>
          <h3 className='font-display font-semibold text-foreground'>
            Notifications
          </h3>
          {unreadCount > 0 && (
            <Button
              variant='link'
              size='sm'
              onClick={() => void markAllAsRead()}
              className='h-auto p-0 text-xs text-brand-cornflower'
            >
              Mark all as read
            </Button>
          )}
        </div>

        {/* Announce arrivals without stealing focus. */}
        <p className='sr-only' aria-live='polite'>
          {unreadCount > 0
            ? `${unreadCount} unread notifications`
            : 'No unread notifications'}
        </p>

        <div className='max-h-[300px] overflow-y-auto p-2'>
          {showSkeleton ? (
            <div className='space-y-1'>
              <NotificationSkeleton />
              <NotificationSkeleton />
              <NotificationSkeleton />
            </div>
          ) : error ? (
            <div className='py-8 text-center'>
              <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10'>
                <Icons.alertCircle
                  className='h-6 w-6 text-destructive'
                  aria-hidden='true'
                />
              </div>
              <p className='text-sm font-medium text-foreground'>
                Could not load notifications
              </p>
              <Button
                variant='link'
                size='sm'
                onClick={() => void refresh()}
                className='mt-1 h-auto p-0 text-xs text-brand-cornflower'
              >
                Try again
              </Button>
            </div>
          ) : notifications.length === 0 ? (
            <div className='py-8 text-center'>
              <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted/50'>
                <Icons.checkCircle
                  className='h-6 w-6 text-muted-foreground'
                  aria-hidden='true'
                />
              </div>
              <p className='text-sm font-medium text-foreground'>
                All caught up
              </p>
              <p className='mt-1 text-xs text-muted-foreground'>
                No new notifications
              </p>
            </div>
          ) : (
            <ul className='space-y-1'>
              {notifications.map((notification) => (
                <li key={notification.id}>
                  <NotificationItem
                    notification={notification}
                    onMarkAsRead={(id) => void markAsRead(id)}
                    onNavigate={() => setOpen(false)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className='border-t border-border/50 p-2'>
          <Button asChild variant='ghost' className='w-full text-brand-cornflower'>
            <Link href='/notifications' onClick={() => setOpen(false)}>
              View all notifications
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
